import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../../api';
import { defaultHero, parseHero, type HeroContent } from '../../config/hero';

/** 后台"首页标题"编辑：主标题三段（中间段为渐变色）+ 副标题 */
export function AdminHeroPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<HeroContent>(defaultHero);
  const [initialized, setInitialized] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const { data: page, isLoading } = useQuery({
    queryKey: ['page', 'home-hero'],
    queryFn: () => api.getPage('home-hero'),
  });

  useEffect(() => {
    if (!initialized && page) {
      setForm(parseHero(page.content_md));
      setInitialized(true);
    }
  }, [page, initialized]);

  const saveMutation = useMutation({
    mutationFn: () => api.savePage('home-hero', JSON.stringify(form)),
    onSuccess: () => {
      setSavedAt(new Date());
      queryClient.invalidateQueries({ queryKey: ['page', 'home-hero'] });
    },
  });

  const set = (key: keyof HeroContent) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [key]: e.target.value });

  if (isLoading || !initialized) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="animate-spin text-indigo-500" size={32} />
      </div>
    );
  }

  const inputCls =
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 dark:border-indigo-500/20 dark:bg-space-800 dark:text-star';
  const labelCls = 'mb-1 block text-xs font-medium text-slate-500 dark:text-star-dim';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mx-auto max-w-3xl px-4 pb-20 pt-24 sm:px-6 lg:px-8"
    >
      <header className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin')}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-star-dim dark:hover:bg-space-800"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 dark:text-star">首页标题</h1>
            <p className="text-sm text-slate-600 dark:text-star-dim">中间一段会以渐变色高亮显示</p>
          </div>
        </div>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-nebula"
        >
          {saveMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          保存
        </button>
      </header>

      {/* 实时预览 */}
      <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-8 dark:border-indigo-500/20 dark:bg-space-800/60">
        <h1 className="mb-4 font-serif text-4xl font-bold tracking-tight text-stone-900 dark:text-star">
          <span>{form.title_pre}</span>
          <span className="glow-text">{form.title_highlight}</span>
          <span>{form.title_post}</span>
        </h1>
        <p className="text-stone-600 dark:text-star-dim">{form.subtitle}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label className={labelCls}>主标题（前段）</label>
          <input value={form.title_pre} onChange={set('title_pre')} className={inputCls} placeholder="写作，是为了" />
        </div>
        <div>
          <label className={labelCls}>主标题（高亮段）</label>
          <input value={form.title_highlight} onChange={set('title_highlight')} className={inputCls} placeholder="更好地" />
        </div>
        <div>
          <label className={labelCls}>主标题（后段）</label>
          <input value={form.title_post} onChange={set('title_post')} className={inputCls} placeholder="思考。" />
        </div>
        <div className="md:col-span-3">
          <label className={labelCls}>副标题</label>
          <textarea
            value={form.subtitle}
            onChange={set('subtitle')}
            rows={2}
            className={inputCls}
            placeholder="一句话介绍这个博客"
          />
        </div>
      </div>

      {savedAt && (
        <p className="mt-4 text-xs text-slate-500 dark:text-star-dim">已保存 {savedAt.toLocaleTimeString()}</p>
      )}
    </motion.div>
  );
}
